import { useMemo, useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useApp, amountValue } from "@/store";
import { useBootstrap, useCreateExpense, CREATE_EXPENSE_KEY } from "@/api/queries";
import { useToast } from "@/components/Toast";
import { CurrencyFlag } from "@/components/Currency";
import { haptic } from "@/lib/telegram";
import { todayISO, fmt } from "@/lib/utils";
import { rankSuggestions, filterSuggestions, NOTE_SCREEN_LIMIT } from "@/lib/suggestions";

/**
 * Экран описания траты. Единственное место с системной клавиатурой — но layout
 * заранее заложен под неё: шапка сверху + textarea flex-1 в контейнере высотой =
 * Telegram viewport. Клавиатура занимает низ, textarea сжимается, ничего не прыгает.
 *
 * SPEC-056: сюда переехало САМО СОХРАНЕНИЕ новой траты. Тап по категории на главном
 * экране больше не пишет запись, а открывает этот экран — здесь уже известна
 * категория, и подсказки берутся только из её истории (63% попадания против 41% у
 * прежнего глобального списка на главном). Тап по подсказке сохраняет сразу: ради
 * этих двух тапов на типовую трату всё и затевалось.
 *
 * Правка существующей траты (editingId) идёт прежним путём: экран только меняет
 * текст в драфте, запись обновляет EditScreen.
 */
export function NoteScreen() {
    const { s, d } = useApp();
    const { data } = useBootstrap();
    const create = useCreateExpense();
    // Счётчик активных мутаций живёт в кэше react-query, а не в компоненте: он спадёт
    // сам, даже если экран размонтируют, поэтому «вечной» блокировки быть не может —
    // в отличие от флага в сторе, который некому было бы снять.
    const saving = useIsMutating({ mutationKey: CREATE_EXPENSE_KEY }) > 0;
    const qc = useQueryClient();
    /** Свежий ответ кэша, а не значение из рендера: между стартом записи и ре-рендером
     *  обработчик успел бы отпустить экран. */
    const isSavingNow = () => qc.isMutating({ mutationKey: CREATE_EXPENSE_KEY }) > 0;
    const toast = useToast();
    const [text, setText] = useState(s.note);

    const isEditing = s.editingId !== null;
    const category = (data?.categories ?? []).find(c => c.id === s.categoryId) ?? null;
    const account = (data?.accounts ?? []).find(a => a.id === s.accountId) ?? null;

    const ranked = useMemo(
        () => rankSuggestions(data?.expenses ?? [], todayISO(), s.categoryId),
        [data?.expenses, s.categoryId],
    );
    const shown = filterSuggestions(ranked, text, NOTE_SCREEN_LIMIT);

    /** Правка траты: экран только отдаёт текст обратно в драфт (запись — в EditScreen). */
    const saveDraftNote = () => {
        haptic("light");
        d({ t: "note", v: text.trim() });
        d({ t: "screen", v: "edit" });
    };

    /** Создание: пишем трату с этим описанием. Пустое `note` — трата без комментария. */
    const createExpense = (note: string) => {
        if (isSavingNow()) return;
        const amount = amountValue(s);
        // Пока запись летит, уйти с экрана нельзя ни стрелкой, ни нативной кнопкой
        // Telegram: иначе компонент размонтируется, onSuccess не выполнится, draftId
        // останется использованным — и СЛЕДУЮЩАЯ трата уйдёт с тем же id, а сервер
        // молча ответит «уже сохранено» (INSERT OR IGNORE).
        const catName = category?.name ?? "";
        create.mutate(
            // created_at не шлём: сервер ставит datetime('now') (SPEC-024 G3); s.date — локальный день.
            // MA-01 (SPEC-042): id живёт в драфте — ретрай и двойной тап шлют тот же UUID.
            // SPEC-032: осознанный override валюты сохраняется как раньше.
            {
                id: s.draftId, date: s.date, amount, currency: s.currency,
                category_id: s.categoryId, account_id: s.accountId, note: note.trim() || null,
                allow_currency_mismatch: !!account && s.currency !== account.currency,
            },
            {
                onSuccess: (resp) => {
                    haptic("success");
                    toast(resp.inserted ? `✓ ${fmt(amount, s.currency)} ${s.currency} → ${catName}` : "Уже сохранено");
                    d({ t: "resetDraft" });
                    d({ t: "screen", v: "main" });
                },
                // Экран остаётся открытым: набранный текст не теряется, ретрай идёт тем же draftId.
                onError: (e) => { haptic("error"); toast(e instanceof Error ? e.message : "Ошибка сохранения", "err"); },
            },
        );
    };

    const cancel = () => {
        if (isSavingNow()) return;         // запись летит — экран не отпускаем
        haptic("light");
        if (isEditing) { d({ t: "screen", v: "edit" }); return; }
        // Категорию снимаем: она была выбрана только ради этого экрана (E2).
        d({ t: "category", v: null });
        d({ t: "screen", v: "main" });
    };

    const confirm = () => (isEditing ? saveDraftNote() : createExpense(text));
    const primaryLabel = isEditing || text.trim() ? "Готово" : "Без описания";

    return (
        <div className="flex flex-col" style={{ height: "var(--tg-viewport-height, 100dvh)" }}>
            <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border/60 shrink-0">
                <button onClick={cancel} aria-label="Назад" disabled={saving}
                    className="text-hint py-1 -ml-1 disabled:opacity-40">
                    <ArrowLeft className="h-5 w-5" />
                </button>
                {/* Контекст записи прямо в шапке: владелец видит, что именно сохраняет. */}
                <span className="min-w-0 truncate text-center font-semibold">
                    {isEditing ? "Описание" : (
                        <>
                            {category?.emoji ? `${category.emoji} ` : ""}{category?.name ?? "Трата"}
                            <span className="text-hint font-normal">
                                {" · "}{fmt(amountValue(s), s.currency)} <CurrencyFlag code={s.currency} /> {s.currency}
                            </span>
                        </>
                    )}
                </span>
                <button onClick={confirm} disabled={saving}
                    className="inline-flex items-center gap-1.5 shrink-0 text-accent font-medium text-sm py-1 disabled:opacity-50">
                    {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-label="Сохраняется" />}
                    {primaryLabel}
                </button>
            </header>
            {/* Подсказки живут НАД полем: клавиатура занимает низ экрана и перекрыла бы
                любой список, отрисованный под textarea. */}
            {shown.length > 0 && (
                <div className="flex flex-wrap gap-2 px-4 py-2.5 border-b border-border/60 shrink-0 max-h-24 overflow-y-auto">
                    {shown.map(({ note }) => (
                        <button
                            key={note}
                            disabled={saving}
                            // При создании тап по подсказке — это и есть сохранение (второй тап
                            // после категории). При правке — просто подстановка текста в поле.
                            onClick={() => { haptic("light"); if (isEditing) setText(note); else createExpense(note); }}
                            className="py-1.5 px-3 rounded-full text-xs font-medium bg-secondary-bg text-hint active:animate-pop disabled:opacity-50">
                            {note}
                        </button>
                    ))}
                </div>
            )}
            <textarea
                autoFocus
                value={text}
                enterKeyHint="done"
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); confirm(); } }}
                placeholder="Введите описание покупки"
                className="flex-1 w-full px-4 py-3 text-base bg-transparent outline-none resize-none"
            />
        </div>
    );
}
